import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { GLOBAL_AGENTS_SID } from "@app/types/assistant/assistant";
import { POD_TASK_STATUSES } from "@app/types/project_task";
import { z } from "zod";

export const MY_TASKS_SERVER_NAME = "my_tasks" as const;
export const MY_TASKS_LIST_TOOL_NAME = "list_my_tasks";
export const MY_TASKS_CREATE_TOOL_NAME = "create_task";
export const MY_TASKS_UPDATE_TOOL_NAME = "update_task";
export const MY_TASKS_START_TOOL_NAME = "start_task";
export const MY_TASKS_DISMISS_TOOL_NAME = "dismiss_task";
export const MY_TASKS_START_WORK_TOOL_NAME = "start_work";
export const MY_TASKS_START_TEAM_THREAD_TOOL_NAME = "start_team_thread";

const taskId = z
  .string()
  .describe("The task's `id`, as list_my_tasks returned it.");

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD")
  .describe("A calendar date, YYYY-MM-DD, in the user's timezone.");

export const MY_TASKS_TOOLS_METADATA = [
  {
    name: MY_TASKS_LIST_TOOL_NAME,
    description:
      "List the current user's own tasks exactly as their My Tasks page shows them: tasks identified for them that are waiting for their review, their open tasks, what they finished recently, and work they handed to colleagues. Call this whenever the user asks about their tasks, to-dos, what they should work on, or what is waiting on them. Works in any conversation. Each section gives its full `total`; when it is larger than the tasks listed, say how many there are and point to the My Tasks page for the rest.",
    schema: {},
    stake: "never_ask",
    eager: true,
    displayLabels: {
      running: "Reading your tasks",
      done: "Read your tasks",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MY_TASKS_CREATE_TOOL_NAME,
    description:
      "Add a task to the current user's My Tasks list, optionally with a due date. Use when they say to add, note or remember something they have to do.",
    schema: {
      text: z
        .string()
        .min(1)
        .max(500)
        .describe("The task, as they would write it."),
      dueOn: isoDate.optional(),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Adding a task",
      done: "Added a task",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MY_TASKS_UPDATE_TOOL_NAME,
    description:
      "Update one of the current user's tasks: mark it done or reopen it, snooze it until a date (or unsnooze), set or clear its due date, or move it to another status. Pass only the fields that change.",
    schema: {
      taskId,
      done: z
        .boolean()
        .optional()
        .describe("true marks it done, false reopens it."),
      snoozedUntil: isoDate
        .nullable()
        .optional()
        .describe("Hide it until this date; null wakes it up now."),
      dueOn: isoDate
        .nullable()
        .optional()
        .describe("null clears the due date."),
      status: z.enum(POD_TASK_STATUSES).optional(),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Updating a task",
      done: "Updated a task",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MY_TASKS_START_TOOL_NAME,
    description:
      "Accept a task identified for the current user, or start work on one of their open tasks, so their chief of staff picks it up in its own conversation. Optional instructions steer that work.",
    schema: {
      taskId,
      instructions: z
        .string()
        .max(2000)
        .optional()
        .describe("What to do or keep in mind, in the user's words."),
    },
    stake: "low",
    displayLabels: {
      running: "Starting a task",
      done: "Started a task",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MY_TASKS_START_WORK_TOOL_NAME,
    description:
      "Move a new, multi-step aim out of this direct conversation into its own work thread, tracked on the user's My Tasks. Only after the user said yes to tracking it separately. Refused outside the user's direct conversation with you.",
    schema: {
      title: z
        .string()
        .min(1)
        .max(200)
        .describe("The aim, as a short title: 'Q4 marketing campaign plan'."),
      brief: z
        .string()
        .min(1)
        .max(4000)
        .describe(
          "What the work is, what done looks like, and what was already said here."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Opening a work thread",
      done: "Opened a work thread",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MY_TASKS_START_TEAM_THREAD_TOOL_NAME,
    description:
      "Open a team thread for a large multi-stage job such as lead generation: a new conversation with the user, you and the chosen teammates, with its own shared board. Only after the user said yes to it. Refused outside the user's direct conversation with you or a Pod they can write to.",
    schema: {
      title: z
        .string()
        .min(1)
        .max(200)
        .describe("The thread's title: 'Leads Discovery · memoair.ai'."),
      brief: z
        .string()
        .min(1)
        .max(4000)
        .describe(
          "What the job is, what done looks like, and what was already said here."
        ),
      teammates: z
        .array(
          z.enum([
            GLOBAL_AGENTS_SID.RESEARCHER,
            GLOBAL_AGENTS_SID.LEAD_SCOUT,
            GLOBAL_AGENTS_SID.FOLLOW_UP,
          ])
        )
        .min(1)
        .max(3)
        .describe("The teammates to bring in."),
    },
    stake: "low",
    displayLabels: {
      running: "Opening a team thread",
      done: "Opened a team thread",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MY_TASKS_DISMISS_TOOL_NAME,
    description:
      "Dismiss a task identified for the current user that they do not want, or delete one of their own tasks. Use when they say not now, drop it, or remove it.",
    schema: { taskId },
    stake: "low",
    displayLabels: {
      running: "Dismissing a task",
      done: "Dismissed a task",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const MY_TASKS_SERVER = {
  serverInfo: {
    name: MY_TASKS_SERVER_NAME,
    version: "1.0.0",
    description:
      "Read and change the current user's own tasks from their My Tasks page: list, add, mark done, snooze, set due dates, start and dismiss.",
    authorization: null,
    icon: "ActionCheckCircleIcon",
    documentationUrl: null,
  },
  tools: MY_TASKS_TOOLS_METADATA,
} as const satisfies ServerMetadata;
